import { useMemo } from 'react';
import { getRemotionEnvironment, Sequence, useVideoConfig } from 'remotion';
import { CaptionsTrack } from '@goldcast/player-config/src/types/RemotionPlayerConfigTypes/Track/CaptionsTrack/CaptionsTrack';
import { ElementsTrack } from '@goldcast/player-config/src/types/RemotionPlayerConfigTypes/Track/ElementsTrack';
import { CompositionConfig } from '@goldcast/player-config/src/types/RemotionPlayerConfigTypes/CompositionConfig';
import { getDefaultFontProperties } from '@goldcast/player-config/src/utils/fontUtils';
import { generateCaptionPages } from '../../libs/caption/generate-caption-pages';
import CaptionsPages from '../Captions/CaptionsPages';
import { useLoadRemotionFont } from '../hooks/useLoadRemotionFont';

export const CaptionsTrackComponent = ({
  track,
  elementTracks,
  onOutlineRelease,
  canEditCaptions = true,
  disableCaptionsDragging = false,
  disableCaptionsResize = false,
  onOutlineResize
}: {
  track: CaptionsTrack;
  elementTracks?: ElementsTrack[];
  onOutlineRelease: CompositionConfig['onOutlineRelease'];
  canEditCaptions?: boolean;
  disableCaptionsDragging?: boolean;
  disableCaptionsResize?: boolean;
  onOutlineResize: CompositionConfig['onOutlineResize'];
}) => {
  const isPreview = getRemotionEnvironment().isPlayer;
  const { fps } = useVideoConfig();
  const font = track.font;

  const { isPending } = useLoadRemotionFont({ font });

  const captionPages = useMemo(() => {
    if (!track.metadata) {
      return track.pages ?? [];
    }
    if (isPending) {
      return [];
    }
    return generateCaptionPages({
      metadata: track.metadata,
      elementTracks: elementTracks ?? [],
      fps,
      fontFamily: font?.name ?? getDefaultFontProperties(true).font,
      captionTrackOffset: track.fromFrame
    });
  }, [track.metadata, elementTracks, fps, isPending, track.pages, track.fromFrame, font]);

  return (
    <Sequence from={track.fromFrame} durationInFrames={track.toFrame - track.fromFrame}>
      <CaptionsPages
        pages={captionPages}
        onOutlineRelease={onOutlineRelease}
        canEditCaptions={isPreview && canEditCaptions}
        drift={0}
        font={track.font}
        disableCaptionsDragging={disableCaptionsDragging}
        disableCaptionsResize={disableCaptionsResize}
        onOutlineResize={onOutlineResize}
      />
    </Sequence>
  );
};
